import {
  type IGetTrainingAppliesByCandidateAutocomplete,
  type IPostTrainingAppliesCandidateResponseItem,
  useGetTrainingAppliesByCandidateAutocomplete,
} from "@api";
import type { IFormGenContext, TJSONSchema } from "@artifact-management/json-dynamic-form";
import type { TrainingApplyStatus } from "@bbj-types/apply/training";
import { TRAINING_OFFER_DURATION_UNIT_LABELS } from "@root/utils/constants/trainingOffer";
import { useI18nContext } from "@root/utils/organisation/i18nContext";
import { type MouseEvent, useLayoutEffect, useMemo, useState } from "react";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export type TTrainingApplyAutocomplete =
  | IGetTrainingAppliesByCandidateAutocomplete["data"][number]
  | IPostTrainingAppliesCandidateResponseItem;

export type TTrainingApplyOption = {
  label: string;
  subText: string;
  value: string;
};

export const getTrainingApplySubtext = (trainingApply: TTrainingApplyAutocomplete, timeZone: string) => {
  let text = trainingApply.offer?.provider?.name ?? "";
  if (trainingApply.offer?.address?.city) {
    text += (text ? " . " : "") + trainingApply.offer.address.city;
  }
  const { trainingDuration, trainingStartDate, trainingEndDate } = trainingApply.offer ?? {};
  if (trainingDuration?.duration && trainingDuration.unit) {
    text += `${text ? " . " : ""}${trainingDuration.duration} ${TRAINING_OFFER_DURATION_UNIT_LABELS[trainingDuration.unit]}`;
  } else if (trainingStartDate && trainingEndDate) {
    const days = Math.max(
      1,
      Math.round((new Date(trainingEndDate).getTime() - new Date(trainingStartDate).getTime()) / MS_PER_DAY)
    );
    text += `${text ? " . " : ""}${days} jours`;
  }
  if (trainingStartDate && trainingEndDate) {
    const start = new Date(trainingStartDate).toLocaleDateString("fr-FR", { timeZone });
    const end = new Date(trainingEndDate).toLocaleDateString("fr-FR", { timeZone });
    text += `${text ? " " : ""}(${start} - ${end})`;
  }
  return text;
};

const getTrainingApplyOption = (trainingApply: TTrainingApplyAutocomplete, timeZone: string): TTrainingApplyOption => {
  return {
    label: trainingApply.offer.name,
    subText: getTrainingApplySubtext(trainingApply, timeZone),
    value: trainingApply._id,
  };
};

export const useFormGenInputTrainingApply = (
  trainingApplyOptions: Exclude<TJSONSchema["trainingApply"], undefined>,
  context: IFormGenContext | undefined
) => {
  const { timeZone } = useI18nContext();
  const [{ data, loading }, getTrainingApplies] = useGetTrainingAppliesByCandidateAutocomplete();
  const [trainingApplies, setTrainingApplies] = useState<TTrainingApplyAutocomplete[]>([]);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);

  const options = useMemo(() => {
    return trainingApplies.map((trainingApply) => getTrainingApplyOption(trainingApply, timeZone));
  }, [trainingApplies, timeZone]);

  const handleNewApplyClick = (evt: MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setAutocompleteOpen(false);
    setApplyModalOpen(true);
  };

  const handleNewApply = (trainingApply: IPostTrainingAppliesCandidateResponseItem): TTrainingApplyOption => {
    const option = getTrainingApplyOption(trainingApply, timeZone);
    setTrainingApplies((prev) => [
      trainingApply,
      ...prev.filter((currentTrainingApply) => currentTrainingApply._id !== trainingApply._id),
    ]);
    setApplyModalOpen(false);
    return option;
  };

  useLayoutEffect(() => {
    if (!context?.candidateId) {
      return;
    }
    void getTrainingApplies(context.candidateId, { status: trainingApplyOptions.status as TrainingApplyStatus[] });
  }, [context?.candidateId, trainingApplyOptions.status, getTrainingApplies]);

  useLayoutEffect(() => {
    if (data) {
      setTrainingApplies(data.data);
    }
  }, [data]);

  return {
    autocompleteOpen,
    setAutocompleteOpen,
    handleNewApplyClick,
    options,
    loading,
    applyModalOpen,
    setApplyModalOpen,
    handleNewApply,
  };
};
